/**
 * Derive intrinsic-value lab inputs and time series from /fundamentals API shape.
 */
import { alignDateToInterval, getFundDate, toEpoch } from "../indicators/engine";
import { parseAaaYieldApiResponse } from "./fundamentalMetrics";
import { computeIntrinsicForModel, defaultValuesForModel } from "./intrinsicValueModels";

const LAG_DAYS = 45;
const INTERVAL = "quarter";
/** Trailing window for Graham g smoothing (12 calendar months in seconds). */
const GRAHAM_G_ROLLING_SEC = 365.25 * 24 * 3600;

function sortByStmtDate(arr) {
  return [...(arr || [])].sort((a, b) => getFundDate(a) - getFundDate(b));
}

function rowKey(r) {
  return r?.fiscalDateEnding || r?.date || "";
}

function ttmSum(rows, field, endIdx) {
  let s = 0;
  for (let k = endIdx - 3; k <= endIdx; k++) s += Number(rows[k]?.[field] ?? 0) || 0;
  return s;
}

function netDebtDollars(bs) {
  if (!bs) return null;
  const debt =
    bs.totalDebt ??
    (bs.longTermDebt != null || bs.shortTermDebt != null
      ? Number(bs.longTermDebt || 0) + Number(bs.shortTermDebt || 0)
      : null);
  const cash = bs.cashAndCashEquivalents ?? bs.cashAndShortTermInvestments ?? bs.totalCash ?? bs.cash ?? 0;
  const d = debt != null ? Number(debt) : null;
  if (d == null) return null;
  return d - Number(cash || 0);
}

function effectiveTaxRate(incomeRows, endIdx) {
  let tti = 0;
  let tttax = 0;
  for (let k = endIdx - 3; k <= endIdx; k++) {
    const ibt = incomeRows[k]?.incomeBeforeTax ?? incomeRows[k]?.pretaxIncome ?? null;
    const tax = incomeRows[k]?.incomeTaxExpense ?? null;
    if (ibt != null && tax != null && Number(ibt) !== 0) {
      tti += Math.abs(Number(ibt));
      tttax += Math.abs(Number(tax));
    }
  }
  if (tti > 0 && tttax >= 0) return Math.min(0.35, Math.max(0, tttax / tti));
  return 0.21;
}

function roeDecimal(ttmNi, equity) {
  if (!(equity > 0) || ttmNi == null) return null;
  return ttmNi / equity;
}

function epsFromAnnualRow(row) {
  const v = row?.epsdiluted ?? row?.epsDiluted ?? row?.dilutedEPS ?? row?.eps;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Reference fiscal/calendar year for Graham windows (from quarterly income row). */
function fiscalRefYearFromIncomeRow(inc, fiscalKey) {
  if (inc?.calendarYear != null && Number.isFinite(Number(inc.calendarYear))) return Number(inc.calendarYear);
  const s = String(fiscalKey || "").slice(0, 4);
  const y = parseInt(s, 10);
  return Number.isFinite(y) ? y : null;
}

/**
 * One value per calendar year from annual income (later filings overwrite earlier for the same year).
 */
function buildAnnualDilutedEpsByYearAll(annualIncome) {
  const rows = sortByStmtDate(annualIncome || []);
  const byY = {};
  for (const row of rows) {
    const cy = Number(row.calendarYear);
    if (!Number.isFinite(cy)) continue;
    const eps = epsFromAnnualRow(row);
    if (eps == null) continue;
    byY[cy] = eps;
  }
  return byY;
}

/**
 * Annual EPS by calendar year using only annual statements whose report date is on or before asOfSec.
 */
function buildAnnualDilutedEpsByYearAsOf(annualIncome, asOfSec) {
  const asOfMs = asOfSec * 1000;
  const rows = sortByStmtDate((annualIncome || []).filter((r) => getFundDate(r).getTime() <= asOfMs));
  const byY = {};
  for (const row of rows) {
    const cy = Number(row.calendarYear);
    if (!Number.isFinite(cy)) continue;
    const eps = epsFromAnnualRow(row);
    if (eps == null) continue;
    byY[cy] = eps;
  }
  return byY;
}

/**
 * Graham earnings growth: g = (Average2 / Average1)^0.1 − 1
 * Average2 = mean of available EPS in {Y−2, Y−1, Y}; Average1 = mean of available EPS in {Y−12, Y−11, Y−10}.
 * If one or two years are missing in a window, the average uses only the years that have data (at least one per window).
 */
export function grahamGrowthFromEpsWindows(epsByYear, Y) {
  if (Y == null || !Number.isFinite(Y)) return null;
  const years2 = [Y - 2, Y - 1, Y];
  const years1 = [Y - 12, Y - 11, Y - 10];
  const avg = (years) => {
    const vals = years.map((y) => epsByYear[y]).filter((v) => v != null && Number.isFinite(v) && v > 0);
    if (vals.length === 0) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  };
  const avg2 = avg(years2);
  const avg1 = avg(years1);
  if (avg2 == null || avg1 == null || !(avg1 > 0)) return null;
  const g = Math.pow(avg2 / avg1, 0.1) - 1;
  return Number.isFinite(g) ? g : null;
}

/**
 * Quarterly snapshots with TTM figures and per-share fields (times in seconds UTC).
 */
export function buildQuarterlySnapshots(fundamentals) {
  const income = sortByStmtDate(fundamentals?.income || []);
  const cf = sortByStmtDate(fundamentals?.cashflow || []);
  const bs = sortByStmtDate(fundamentals?.balanceSheet || []);
  const bsMap = new Map();
  for (const b of bs) {
    const k = rowKey(b);
    if (k) bsMap.set(k, b);
  }

  const cfMap = new Map();
  for (const c of cf) {
    const k = rowKey(c);
    if (k) cfMap.set(k, c);
  }

  const out = [];
  for (let i = 3; i < income.length; i++) {
    const inc = income[i];
    const key = rowKey(inc);
    if (!key) continue;

    const cfIdx = cf.findIndex((c) => rowKey(c) === key);
    if (cfIdx < 3) continue;

    const fcffTtm = ttmSum(cf, "freeCashFlow", cfIdx);
    const ttmNi = ttmSum(income, "netIncome", i);
    const ttmOp = ttmSum(income, "operatingIncome", i);
    const ttmRev = ttmSum(income, "revenue", i);

    const b = bsMap.get(key);
    const shares =
      inc.weightedAverageShsOutDil ?? inc.weightedAverageShsOut ?? null;
    const equity = b?.totalStockholdersEquity ?? b?.totalShareholdersEquity ?? b?.totalEquity ?? null;

    const nd = netDebtDollars(b);
    const tax = effectiveTaxRate(income, i);
    const nopat = ttmOp != null ? ttmOp * (1 - tax) : null;

    const d = getFundDate(inc);
    d.setDate(d.getDate() + LAG_DAYS);
    const align = alignDateToInterval(d.toISOString().split("T")[0], INTERVAL);
    const time = toEpoch(align);

    const sharesN = Number(shares);
    const bookPs = sharesN > 0 && equity != null ? Number(equity) / sharesN : null;
    const navPs = bookPs;
    const ttmEps = sharesN > 0 && ttmNi != null ? ttmNi / sharesN : null;

    let roe = null;
    if (bookPs != null && ttmNi != null && equity != null && Number(equity) > 0) {
      roe = roeDecimal(ttmNi, Number(equity));
    }

    const refYear = fiscalRefYearFromIncomeRow(inc, key);

    out.push({
      time,
      fiscalKey: key,
      refYear,
      fcff_ttm: fcffTtm,
      fcfe_ttm: fcffTtm,
      net_debt_m: nd != null ? nd / 1e6 : null,
      shares_m: sharesN > 0 ? sharesN / 1e6 : null,
      book_ps: bookPs,
      nav_ps: navPs,
      roe,
      eps_ttm: ttmEps,
      nopat_m: nopat != null ? nopat / 1e6 : null,
      ttm_revenue: ttmRev,
    });
  }

  out.sort((a, b) => a.time - b.time);

  for (let j = 4; j < out.length; j++) {
    const cur = out[j];
    const prev = out[j - 4];
    if (cur.ttm_revenue != null && prev.ttm_revenue > 0) {
      cur.rev_yoy = cur.ttm_revenue / prev.ttm_revenue - 1;
    }
    if (cur.fcff_ttm != null && prev.fcff_ttm > 0) {
      cur.fcf_yoy = cur.fcff_ttm / prev.fcff_ttm - 1;
    }
  }

  return out;
}

function medianPositive(arr) {
  const x = arr.filter((v) => v != null && Number.isFinite(v) && v > -0.5);
  if (!x.length) return null;
  const s = [...x].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
}

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

/**
 * Suggested discount rates and growth from statements + optional metrics + AAA fallback.
 */
export function suggestRatesFromFundamentals(fundamentals, snapshots, aaaParsed) {
  const metrics = sortByStmtDate(fundamentals?.metrics || []);
  const latestM = metrics.length ? metrics[metrics.length - 1] : {};
  let beta = Number(latestM.beta);
  if (!Number.isFinite(beta) || beta <= 0 || beta > 3) beta = 1;

  const rf = 0.04;
  const erp = 0.055;
  const re = clamp(rf + beta * erp, 0.065, 0.14);

  const fcfYoys = snapshots.map((s) => s.fcf_yoy).filter((v) => v != null && Number.isFinite(v));
  const revYoys = snapshots.map((s) => s.rev_yoy).filter((v) => v != null && Number.isFinite(v));

  let gHigh = medianPositive(fcfYoys);
  if (gHigh == null) gHigh = medianPositive(revYoys);
  if (gHigh == null) gHigh = 0.05;
  gHigh = clamp(gHigh, 0.02, 0.12);

  const gTerm = clamp(Math.min(0.025, gHigh * 0.45 + 0.01), 0.015, 0.03);

  const debtToEq = Number(latestM.debtEquityRatio);
  const wDebt = Number.isFinite(debtToEq) && debtToEq > 0 ? clamp(debtToEq / (1 + debtToEq), 0.05, 0.45) : 0.12;
  const rd = 0.045;
  const t = 0.21;
  const wacc = clamp((1 - wDebt) * re + wDebt * rd * (1 - t), 0.07, 0.13);

  let gBook = medianPositive(revYoys);
  if (gBook == null) gBook = 0.03;
  gBook = clamp(gBook * 0.6, 0.015, 0.06);

  const { fallbackPct } = parseAaaYieldApiResponse(aaaParsed || { points: [], fallbackPct: 5 });
  const yAaa = Number.isFinite(fallbackPct) && fallbackPct > 0 ? fallbackPct : 5;

  const epsMapAll = buildAnnualDilutedEpsByYearAll(fundamentals?.annualIncome || []);
  const yearsAvail = Object.keys(epsMapAll).map(Number).filter((n) => Number.isFinite(n));
  const latestY = yearsAvail.length ? Math.max(...yearsAvail) : null;
  let gGraham = latestY != null ? grahamGrowthFromEpsWindows(epsMapAll, latestY) : null;
  if (gGraham == null || !Number.isFinite(gGraham)) gGraham = 0.07;
  gGraham = clamp(gGraham, -0.05, 0.25);

  const rates = {
    forecast_years: 5,
    g_high: gHigh,
    g_terminal: gTerm,
    wacc,
    re,
    g_book: gBook,
    g_graham: gGraham,
    y_aaa: yAaa,
  };

  const notes = [
    `Cost of equity (CAPM-style): r_e ≈ ${rf * 100}% + β(${beta.toFixed(2)})×${erp * 100}% ≈ ${(re * 100).toFixed(2)}%.`,
    `WACC blends r_e with a debt weight from reported debt/equity (heuristic); adjust for your view.`,
    `g_high uses median YoY TTM free-cash-flow growth where available (capped); otherwise revenue YoY.`,
    `Terminal growth is capped below discount rates and tied to g_high.`,
    `Graham g uses annual diluted EPS: Average2 = mean of available EPS in Y−2,Y−1,Y (up to 3 years); Average1 = mean of available EPS in Y−12…Y−10; if a year is missing, the average uses the rest. Then g = (Average2/Average1)^0.1 − 1. AAA yield uses Moody's Aaa (fallback ${yAaa.toFixed(1)}% if the series is empty).`,
  ];

  return { rates, beta, notes };
}

/**
 * For each quarter, compute discount/growth inputs using only fundamentals available through that quarter
 * (point-in-time view — what the model would have looked like "then", not with today's full-sample medians).
 */
export function attachPointInTimeRates(fundamentals, snapshots, aaaParsed) {
  if (!snapshots?.length) return [];
  const metrics = sortByStmtDate(fundamentals?.metrics || []);
  const { points: aaaSorted, fallbackPct } = parseAaaYieldApiResponse(aaaParsed || { points: [], fallbackPct: 5 });
  const annualIncome = fundamentals?.annualIncome || [];

  return snapshots.map((snap, j) => {
    const window = snapshots.slice(0, j + 1);
    const fcfYoys = window.map((s) => s.fcf_yoy).filter((v) => v != null && Number.isFinite(v));
    const revYoys = window.map((s) => s.rev_yoy).filter((v) => v != null && Number.isFinite(v));

    let gHigh = medianPositive(fcfYoys);
    if (gHigh == null) gHigh = medianPositive(revYoys);
    if (gHigh == null) gHigh = 0.05;
    gHigh = clamp(gHigh, 0.02, 0.12);

    const gTerm = clamp(Math.min(0.025, gHigh * 0.45 + 0.01), 0.015, 0.03);

    let gBook = medianPositive(revYoys);
    if (gBook == null) gBook = 0.03;
    gBook = clamp(gBook * 0.6, 0.015, 0.06);

    const epsAsOf = buildAnnualDilutedEpsByYearAsOf(annualIncome, snap.time);
    let gGraham =
      snap.refYear != null ? grahamGrowthFromEpsWindows(epsAsOf, snap.refYear) : null;
    if (gGraham == null || !Number.isFinite(gGraham)) gGraham = 0.07;
    gGraham = clamp(gGraham, -0.05, 0.25);

    const mRow = metricsRowAtOrBefore(metrics, snap.time);
    let beta = Number(mRow?.beta);
    if (!Number.isFinite(beta) || beta <= 0 || beta > 3) beta = 1;

    const rf = 0.04;
    const erp = 0.055;
    const re = clamp(rf + beta * erp, 0.065, 0.14);

    const debtToEq = Number(mRow?.debtEquityRatio);
    const wDebt = Number.isFinite(debtToEq) && debtToEq > 0 ? clamp(debtToEq / (1 + debtToEq), 0.05, 0.45) : 0.12;
    const rd = 0.045;
    const t = 0.21;
    const wacc = clamp((1 - wDebt) * re + wDebt * rd * (1 - t), 0.07, 0.13);

    const yAaaSnap = aaaYieldPercentAt(aaaSorted, snap.time, fallbackPct);

    return {
      ...snap,
      ratesAsOf: {
        forecast_years: 5,
        g_high: gHigh,
        g_terminal: gTerm,
        wacc,
        re,
        g_book: gBook,
        g_graham: gGraham,
        y_aaa: yAaaSnap,
      },
    };
  });
}

function metricsRowAtOrBefore(metricsSorted, tSec) {
  let best = null;
  for (const m of metricsSorted) {
    const tm = metricRowToEpochSec(m);
    if (tm == null) continue;
    if (tm <= tSec) best = m;
    else break;
  }
  return best;
}

function metricRowToEpochSec(m) {
  const d = m?.date || m?.fiscalDateEnding;
  if (!d) return null;
  const s = String(d).slice(0, 10);
  const [Y, M, D] = s.split("-").map(Number);
  if (!Number.isFinite(Y) || !Number.isFinite(M)) return null;
  const dd = Number.isFinite(D) ? D : 1;
  return Date.UTC(Y, M - 1, dd) / 1000;
}

function latestSnapshot(snapshots) {
  if (!snapshots?.length) return null;
  return snapshots[snapshots.length - 1];
}

function mergeFcffFields(snap, rates) {
  const d = { ...defaultValuesForModel("fcff") };
  if (rates) {
    if (rates.forecast_years != null) d.forecast_years = rates.forecast_years;
    if (rates.g_high != null) d.g_high = rates.g_high;
    if (rates.g_terminal != null) d.g_terminal = rates.g_terminal;
    if (rates.wacc != null) d.wacc = rates.wacc;
  }
  if (snap) {
    if (snap.fcff_ttm != null && snap.fcff_ttm > 0) d.fcff1_m = snap.fcff_ttm / 1e6;
    if (snap.net_debt_m != null && Number.isFinite(snap.net_debt_m)) d.net_debt_m = snap.net_debt_m;
    if (snap.shares_m != null && snap.shares_m > 0) d.shares_m = snap.shares_m;
  }
  return d;
}

function mergeFcfeFields(snap, rates) {
  const d = { ...defaultValuesForModel("fcfe") };
  if (rates) {
    if (rates.forecast_years != null) d.forecast_years = rates.forecast_years;
    if (rates.g_high != null) d.g_high = rates.g_high;
    if (rates.g_terminal != null) d.g_terminal = rates.g_terminal;
    if (rates.re != null) d.re = rates.re;
  }
  if (snap) {
    if (snap.fcfe_ttm != null && snap.fcfe_ttm > 0) d.fcfe1_m = snap.fcfe_ttm / 1e6;
    if (snap.shares_m != null && snap.shares_m > 0) d.shares_m = snap.shares_m;
  }
  return d;
}

function mergeRimFields(snap, rates) {
  const d = { ...defaultValuesForModel("rim") };
  if (rates) {
    if (rates.re != null) d.re = rates.re;
    if (rates.g_book != null) d.g_book = rates.g_book;
  }
  if (snap) {
    if (snap.book_ps != null && snap.book_ps > 0) d.book_ps = snap.book_ps;
    if (snap.roe != null && Number.isFinite(snap.roe)) d.roe = clamp(snap.roe, -0.2, 0.8);
  }
  return d;
}

function mergeNavFields(snap) {
  const d = { ...defaultValuesForModel("adjusted_nav") };
  if (snap?.nav_ps != null && snap.nav_ps > 0) d.nav_ps = snap.nav_ps;
  return d;
}

function mergeEpvFields(snap, rates) {
  const d = { ...defaultValuesForModel("epv") };
  if (rates?.wacc != null) d.wacc = rates.wacc;
  if (snap) {
    if (snap.nopat_m != null && snap.nopat_m > 0) d.nopat_m = snap.nopat_m;
    if (snap.shares_m != null && snap.shares_m > 0) d.shares_m = snap.shares_m;
  }
  return d;
}

function mergeGrahamFields(snap, rates) {
  const d = { ...defaultValuesForModel("graham") };
  if (rates) {
    if (rates.g_graham != null) d.g = rates.g_graham;
    if (rates.y_aaa != null) d.y_aaa = rates.y_aaa;
  }
  if (snap) {
    if (snap.eps_ttm != null && snap.eps_ttm > 0) d.eps = snap.eps_ttm;
  }
  return d;
}

/**
 * Auto-filled form values (latest quarter) + suggestions for each model.
 */
export function buildFilledValuesByModel(fundamentals, aaaParsed) {
  const rawSnaps = buildQuarterlySnapshots(fundamentals);
  const snapshots = attachPointInTimeRates(fundamentals, rawSnaps, aaaParsed);
  const { rates, beta, notes } = suggestRatesFromFundamentals(fundamentals, rawSnaps, aaaParsed);
  const snap = latestSnapshot(snapshots);
  const lastRt = snap?.ratesAsOf ?? rates;
  const gGrahamRolling =
    snap != null ? grahamRollingGAtTime(snapshots, snap.time) : null;
  const grahamRates = {
    ...lastRt,
    g_graham: gGrahamRolling != null ? gGrahamRolling : lastRt?.g_graham,
  };

  return {
    snapshots,
    rates,
    beta,
    suggestionNotes: [
      ...notes,
      "Historical chart: each quarter uses rates estimated from data available only up to that quarter (point-in-time β, WACC, growth), plus filing-based TTM cash flows, debt, and shares.",
      "Graham model: g on the chart is a 12-month rolling average of quarter point-in-time g (same EPS and Y as before). Manual “Update chart” uses the numbers in the form.",
    ],
    filledByModel: {
      fcff: mergeFcffFields(snap, lastRt),
      fcfe: mergeFcfeFields(snap, lastRt),
      rim: mergeRimFields(snap, lastRt),
      adjusted_nav: mergeNavFields(snap),
      epv: mergeEpvFields(snap, lastRt),
      graham: mergeGrahamFields(snap, grahamRates),
    },
  };
}

/**
 * Mean of point-in-time Graham g over quarterly snapshots in (tSec − 12 months, tSec].
 * Falls back to mean of all g at or before tSec, then single latest snap g.
 */
export function grahamRollingGAtTime(snapshots, tSec) {
  if (!snapshots?.length) return null;
  const lo = tSec - GRAHAM_G_ROLLING_SEC;
  const inWin = snapshots.filter((s) => s.time <= tSec && s.time >= lo);
  const gsWin = inWin.map((s) => s.ratesAsOf?.g_graham).filter((v) => v != null && Number.isFinite(v));
  if (gsWin.length > 0) {
    return gsWin.reduce((a, b) => a + b, 0) / gsWin.length;
  }
  const before = snapshots.filter((s) => s.time <= tSec);
  const gsAll = before.map((s) => s.ratesAsOf?.g_graham).filter((v) => v != null && Number.isFinite(v));
  if (gsAll.length > 0) {
    return gsAll.reduce((a, b) => a + b, 0) / gsAll.length;
  }
  return null;
}

function aaaYieldPercentAt(sortedPts, tSec, fallbackPct) {
  if (!sortedPts?.length) return fallbackPct;
  let y = fallbackPct;
  for (const p of sortedPts) {
    if (p.time <= tSec) y = p.value;
    else break;
  }
  return y;
}

/**
 * Intrinsic value per share at each price timestamp using fundamentals as-of that date.
 * Uses each snapshot's ratesAsOf (point-in-time) when present; falls back to global rates.
 * Dates strictly before the first snapshot have no fundamentals — intrinsic is null (no back-fill).
 */
export function buildHistoricalIntrinsicSeries(modelId, pxSec, snapshots, rates, aaaPoints, aaaFallbackPct) {
  if (!pxSec?.length || !snapshots?.length) return [];
  const fallback = rates || {};
  const { points: aaaSorted, fallbackPct } = parseAaaYieldApiResponse({
    points: aaaPoints || [],
    fallbackPct: aaaFallbackPct ?? 5,
  });

  const firstFundSec = snapshots[0].time;
  let si = 0;
  const out = [];

  for (const p of pxSec) {
    if (p.time < firstFundSec) {
      out.push([p.time * 1000, null]);
      continue;
    }
    while (si + 1 < snapshots.length && snapshots[si + 1].time <= p.time) si++;
    const snap = snapshots[si];
    const rt = snap?.ratesAsOf ?? fallback;

    const yAaaDaily = aaaYieldPercentAt(aaaSorted, p.time, fallbackPct);
    let fields;

    switch (modelId) {
      case "fcff":
        fields = mergeFcffFields(snap, {
          forecast_years: rt.forecast_years ?? 5,
          g_high: rt.g_high,
          g_terminal: rt.g_terminal,
          wacc: rt.wacc,
        });
        break;
      case "fcfe":
        fields = mergeFcfeFields(snap, {
          forecast_years: rt.forecast_years ?? 5,
          g_high: rt.g_high,
          g_terminal: rt.g_terminal,
          re: rt.re,
        });
        break;
      case "rim":
        fields = mergeRimFields(snap, { re: rt.re, g_book: rt.g_book });
        break;
      case "adjusted_nav":
        fields = mergeNavFields(snap);
        break;
      case "epv":
        fields = mergeEpvFields(snap, { wacc: rt.wacc });
        break;
      case "graham": {
        const gRoll = grahamRollingGAtTime(snapshots, p.time);
        const gUse = gRoll != null ? gRoll : rt.g_graham;
        fields = mergeGrahamFields(snap, {
          g_graham: gUse,
          y_aaa: yAaaDaily,
        });
        break;
      }
      default:
        fields = {};
    }

    const r = computeIntrinsicForModel(modelId, fields);
    const iv = r.ivPerShare;
    out.push([p.time * 1000, Number.isFinite(iv) && iv > 0 ? iv : null]);
  }

  return out;
}

export function lastFiniteValue(pairs) {
  for (let i = pairs.length - 1; i >= 0; i--) {
    const v = pairs[i][1];
    if (v != null && Number.isFinite(v)) return v;
  }
  return null;
}
